// Production-safe logger utility with configurable log levels
// Log levels: debug < info < warn < error
// Set VITE_LOG_LEVEL environment variable to control output
// Default: warn (only warnings and errors in development)
// Production: always error level regardless of VITE_LOG_LEVEL

const isDev = import.meta.env.DEV;

// Log level hierarchy: debug < info < warn < error
const levels = { debug: 0, info: 1, warn: 2, error: 3 };

// Get configured log level from environment variable
// Default to 'warn' in dev (clean console), 'error' in production
const logLevel = import.meta.env.VITE_LOG_LEVEL || (isDev ? 'warn' : 'error');
const currentLevel = levels[logLevel] || levels.warn;

const logger = {
  // General log - only in development at debug level
  log: (...args) => {
    if (isDev && currentLevel <= levels.debug) {
      console.log(...args);
    }
  },

  // Debug logging - most verbose, only when VITE_LOG_LEVEL=debug
  debug: (...args) => {
    if (isDev && currentLevel <= levels.debug) {
      console.log('[DEBUG]', ...args);
    }
  },

  // Info logging - important operations, VITE_LOG_LEVEL=info or debug
  info: (...args) => {
    if (isDev && currentLevel <= levels.info) {
      console.log('[INFO]', ...args);
    }
  },

  // Warning logging - potential issues, shown by default
  warn: (...args) => {
    if (currentLevel <= levels.warn) {
      console.warn('[WARN]', ...args);
    }
  },

  // Error logging - always shown
  // Note: Error tracking is handled by PostHog via monitoring.js trackError()
  error: (...args) => {
    console.error('[ERROR]', ...args);
  },

  // Grouped logging - useful for collapsing related debug logs
  group: (label) => {
    if (isDev && currentLevel <= levels.debug && console.group) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (isDev && currentLevel <= levels.debug && console.groupEnd) {
      console.groupEnd();
    }
  },

  // Table logging - for structured data viewing
  table: (data) => {
    if (isDev && currentLevel <= levels.debug && console.table) {
      console.table(data);
    }
  },

  // Performance timing - for debugging slow operations
  time: (label) => {
    if (isDev && currentLevel <= levels.debug && console.time) {
      console.time(label);
    }
  },

  timeEnd: (label) => {
    if (isDev && currentLevel <= levels.debug && console.timeEnd) {
      console.timeEnd(label);
    }
  }
};

export default logger;
